import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api';
import { useToast } from '../../shell/toast';
import { Checkbox, Select, Table, TextInput } from '../../shell/kit';
import { formatMoney, formatMoment } from '../../shell/format';
import { CustomerPicker, VehiclePicker } from '../../shell/pickers';
import {
  addVehicle,
  changeVehicle,
  customerVehicles,
  enroll,
  fetchCatalog,
  fetchRetention,
  removeVehicle,
  runRetentionNow,
  saveRetention,
  subscriptionVehicles,
  type CatalogPlan,
  type CustomerVehicle,
  type RetentionScenario,
  type RetentionTouch,
  type SubscriptionVehicle,
} from './api';
import type { BillingKey, BillingTranslate } from './strings';

/**
 * Оформление подписки, состав подписки по машинам и сценарии удержания.
 *
 * Оформить подписку прежде было нельзя ни в одном интерфейсе: маршрут
 * `POST /api/v1/subscriptions` существовал, но ни админка, ни касса его не
 * вызывали (ТЗ §4.2.1). Смена машины и семейный план (§5.3.5, М4.9) и
 * сценарии удержания (§4.2.2) — здесь же, рядом с карточкой подписки.
 */

const INPUT = 'w-full';
const BUTTON =
  'inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none';
const GHOST =
  'inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint';

function Panel({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface p-4 shadow-card">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Label({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs text-ink-muted">{text}</span>
      {children}
    </label>
  );
}

function useGuard(): (run: () => Promise<void>) => Promise<boolean> {
  const toast = useToast();
  return useCallback(
    async (run) => {
      try {
        await run();
        return true;
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : String(err),
          err instanceof ApiError ? err.code : undefined,
        );
        return false;
      }
    },
    [toast],
  );
}

/* --------------------------------------------------------------- оформление */

export function SellTab({ t }: { t: BillingTranslate }) {
  const toast = useToast();
  const guard = useGuard();
  const [plans, setPlans] = useState<readonly CatalogPlan[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [vehicles, setVehicles] = useState<readonly CustomerVehicle[] | null>(null);
  const [vehicleId, setVehicleId] = useState('');
  const [planId, setPlanId] = useState('');
  const [autorenew, setAutorenew] = useState(true);
  const [chargeNow, setChargeNow] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void guard(async () => {
      const list = await fetchCatalog();
      setPlans(list);
      setPlanId((current) => current || list[0]?.id || '');
    });
  }, [guard]);

  async function pickCustomer(id: string): Promise<void> {
    setCustomerId(id);
    setVehicles(null);
    setVehicleId('');
    if (!id) return;
    await guard(async () => {
      const list = await customerVehicles(id);
      setVehicles(list);
      setVehicleId(list[0]?.id ?? '');
    });
  }

  async function submit(): Promise<void> {
    setBusy(true);
    const ok = await guard(async () => {
      const result = await enroll({ customerId, planId, vehicleId, autorenew, chargeNow });
      toast.ok(t('billing.sell.done', { status: result.subscription.status }));
    });
    if (ok) {
      setCustomerId('');
      setVehicles(null);
      setVehicleId('');
    }
    setBusy(false);
  }

  const plan = plans.find((p) => p.id === planId);

  return (
    <Panel title={t('billing.sell.title')} hint={t('billing.sell.hint')}>
      <div className="grid gap-3 sm:grid-cols-3">
        <Label text={t('billing.sell.customer')}>
          <CustomerPicker value={customerId} onChange={(id) => void pickCustomer(id)} />
        </Label>
        <Label text={t('billing.sell.vehicle')}>
          <Select
            className={INPUT}
            value={vehicleId}
            disabled={!vehicles || vehicles.length === 0}
            onChange={(e) => setVehicleId(e.target.value)}
          >
            {(vehicles ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate} · {v.role}
              </option>
            ))}
          </Select>
        </Label>
        <Label text={t('billing.sell.plan')}>
          <Select className={INPUT} value={planId} onChange={(e) => setPlanId(e.target.value)}>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {formatMoney(p.priceMinor)}
                {p.limits.maxVehicles && p.limits.maxVehicles > 1 ? ` · ${t('billing.plans.maxVehicles')}: ${p.limits.maxVehicles}` : ''}
              </option>
            ))}
          </Select>
        </Label>
      </div>
      {vehicles && vehicles.length === 0 ? (
        <p className="mt-2 text-sm text-warn">{t('billing.sell.noVehicles')}</p>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2">
          <Checkbox checked={autorenew} onChange={(e) => setAutorenew(e.target.checked)} />
          {t('billing.sell.autorenew')}
        </label>
        <label className="flex items-center gap-2">
          <Checkbox checked={chargeNow} onChange={(e) => setChargeNow(e.target.checked)} />
          {t('billing.sell.chargeNow')}
          {plan ? ` (${formatMoney(plan.priceMinor)})` : ''}
        </label>
      </div>
      <div className="mt-3">
        <button
          type="button"
          className={BUTTON}
          disabled={busy || !customerId || !vehicleId || !planId}
          onClick={() => void submit()}
        >
          {t('billing.sell.submit')}
        </button>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------ состав подписки */

export function VehiclesPanel({
  t,
  subscriptionId,
  onChanged,
}: {
  t: BillingTranslate;
  subscriptionId: string;
  onChanged: () => void;
}) {
  const toast = useToast();
  const guard = useGuard();
  const [rows, setRows] = useState<readonly SubscriptionVehicle[]>([]);
  const [target, setTarget] = useState('');
  const [release, setRelease] = useState(true);
  const [member, setMember] = useState('');
  const [asDriver, setAsDriver] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    await guard(async () => setRows(await subscriptionVehicles(subscriptionId)));
  }, [guard, subscriptionId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(run: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    const ok = await guard(async () => {
      await run();
      toast.ok(t('billing.vehicles.saved'));
    });
    if (ok) {
      setTarget('');
      setMember('');
      await load();
      onChanged();
    }
    setBusy(false);
  }

  return (
    <div className="mt-4">
      <h4 className="text-sm font-semibold text-ink">{t('billing.vehicles.title')}</h4>
      <ul className="mt-1 space-y-1 text-sm">
        {rows.map((row) => (
          <li key={`${row.vehicleId}-${row.addedAt}`} className="flex items-center gap-2">
            <span className={row.removedAt ? 'text-ink-faint line-through' : 'font-medium'}>{row.plate}</span>
            <span className="text-xs text-ink-muted">
              {t(`billing.vehicles.${row.role}` as BillingKey)}
              {row.removedAt
                ? ` · ${t('billing.vehicles.removed', { when: formatMoment(row.removedAt), reason: row.removeReason ?? '' })}`
                : ''}
              {!row.removedAt && !row.linked ? ` · ${t('billing.vehicles.unlinked')}` : ''}
            </span>
            {!row.removedAt && row.role === 'member' ? (
              <button
                type="button"
                className={GHOST}
                disabled={busy}
                onClick={() =>
                  void act(() => removeVehicle(subscriptionId, row.vehicleId, t('billing.vehicles.removeReason')))
                }
              >
                {t('billing.vehicles.remove')}
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label text={t('billing.vehicles.change')}>
            <VehiclePicker value={target} onChange={(id) => setTarget(id)} />
          </Label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={release} onChange={(e) => setRelease(e.target.checked)} />
            {t('billing.vehicles.release')}
          </label>
          <button
            type="button"
            className={GHOST}
            disabled={busy || !target}
            onClick={() => void act(() => changeVehicle(subscriptionId, target, release))}
          >
            {t('billing.vehicles.changeSubmit')}
          </button>
        </div>
        <div className="space-y-2">
          <Label text={t('billing.vehicles.add')}>
            <VehiclePicker value={member} onChange={(id) => setMember(id)} />
          </Label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={asDriver} onChange={(e) => setAsDriver(e.target.checked)} />
            {t('billing.vehicles.asDriver')}
          </label>
          <button
            type="button"
            className={GHOST}
            disabled={busy || !member}
            onClick={() => void act(() => addVehicle(subscriptionId, member, asDriver))}
          >
            {t('billing.vehicles.addSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- удержание */

function ScenarioRow({
  t,
  scenario,
  onSaved,
}: {
  t: BillingTranslate;
  scenario: RetentionScenario;
  onSaved: (next: RetentionScenario) => void;
}) {
  const toast = useToast();
  const guard = useGuard();
  const [draft, setDraft] = useState(scenario);
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft(scenario), [scenario]);

  async function save(): Promise<void> {
    setBusy(true);
    await guard(async () => {
      onSaved(await saveRetention(draft));
      toast.ok(t('billing.retention.saved'));
    });
    setBusy(false);
  }

  return (
    <tr className="border-t border-line align-top">
      <td className="py-2 pr-2 font-medium">{t(`billing.retention.code.${scenario.code}` as BillingKey)}</td>
      <td className="py-2 pr-2">
        <Checkbox checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
      </td>
      <td className="w-24 py-2 pr-2">
        <TextInput
          type="number"
          min={0}
          value={String(draft.delayDays)}
          onChange={(e) => setDraft({ ...draft, delayDays: Math.max(0, Number(e.target.value) || 0) })}
        />
      </td>
      <td className="w-24 py-2 pr-2">
        <TextInput
          type="number"
          min={0}
          value={String(draft.cooldownDays)}
          onChange={(e) => setDraft({ ...draft, cooldownDays: Math.max(0, Number(e.target.value) || 0) })}
        />
      </td>
      <td className="py-2 pr-2">
        <TextInput value={draft.offer} onChange={(e) => setDraft({ ...draft, offer: e.target.value })} />
      </td>
      <td className="py-2">
        <button type="button" className={GHOST} disabled={busy} onClick={() => void save()}>
          {t('billing.retention.save')}
        </button>
      </td>
    </tr>
  );
}

export function RetentionTab({ t }: { t: BillingTranslate }) {
  const toast = useToast();
  const guard = useGuard();
  const [scenarios, setScenarios] = useState<readonly RetentionScenario[]>([]);
  const [touches, setTouches] = useState<readonly RetentionTouch[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    await guard(async () => {
      const data = await fetchRetention();
      setScenarios(data.scenarios);
      setTouches(data.touches);
    });
  }, [guard]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(): Promise<void> {
    setBusy(true);
    await guard(async () => {
      const result = await runRetentionNow();
      toast.ok(t('billing.retention.ran', { total: result.total }));
      await load();
    });
    setBusy(false);
  }

  return (
    <div className="space-y-4">
      <Panel title={t('billing.retention.title')} hint={t('billing.retention.hint')}>
        <Table>
          <thead className="text-left text-xs text-ink-muted">
            <tr>
              <th className="py-1">{t('billing.retention.scenario')}</th>
              <th>{t('billing.retention.enabled')}</th>
              <th>{t('billing.retention.delay')}</th>
              <th>{t('billing.retention.cooldown')}</th>
              <th>{t('billing.retention.offer')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {scenarios.map((scenario) => (
              <ScenarioRow
                key={scenario.code}
                t={t}
                scenario={scenario}
                onSaved={(next) =>
                  setScenarios((list) => list.map((s) => (s.code === next.code ? next : s)))
                }
              />
            ))}
          </tbody>
        </Table>
        <div className="mt-3">
          <button type="button" className={BUTTON} disabled={busy} onClick={() => void run()}>
            {t('billing.retention.run')}
          </button>
        </div>
      </Panel>

      <Panel title={t('billing.retention.touches')}>
        {touches.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('billing.retention.noTouches')}</p>
        ) : (
          <Table>
            <thead className="text-left text-xs text-ink-muted">
              <tr>
                <th className="py-1">{t('billing.retention.when')}</th>
                <th>{t('billing.retention.scenario')}</th>
                <th>{t('billing.retention.customer')}</th>
              </tr>
            </thead>
            <tbody>
              {touches.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="py-1 text-xs">{formatMoment(row.createdAt)}</td>
                  <td>{t(`billing.retention.code.${row.scenario}` as BillingKey)}</td>
                  <td>{row.customerName ?? row.customerId}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
    </div>
  );
}
